/**
 * web/src/lib/promo.ts — promo-code redemption, web half (card KpYLZuhhlcvC).
 *
 * Pear's promo codes (shared/billing.ts PROMO_CODES) are founder-managed and
 * already redeem over POST /api/billing/promo — what has been missing is any
 * place a user can type one. This module adds the link: the account-creation
 * screen's promo-code field captures the code, and once this device holds a
 * couple token the stored code is redeemed.
 *
 * The shape deliberately mirrors referrals (lib/referrals.ts): a promo grant
 * is also given, not bought, and the same rules apply —
 *   - capture first (screen field), apply later (once a couple token exists),
 *   - silent on every refusal (a dead / wrong-account code must never look
 *     like a broken sign-up),
 *   - per-couple applied marker, so signing out and forming another couple
 *     can still redeem a fresh code (or the same one, if their account allows),
 *   - the code is kept only while it might still land; a terminal refusal
 *     (invalid / already redeemed / wrong account) drops it.
 *
 * localStorage keys (dotted, beside the snake key the app owns):
 *   pear.promo         the captured code, waiting to be redeemed
 *   pear.promo.applied the code+couple already redeemed (or refused for good)
 */

import { api, getToken } from './api';

/** Fired on window after a successful apply — PromoToast names the reward. */
export const PROMO_APPLIED_EVENT = 'pear:promo-applied';

const LS_PROMO = 'pear.promo';
const LS_PROMO_APPLIED = 'pear.promo.applied';

/** A refusal that is terminal for this couple: forget the code so it can
 *  never fire again. Network failures are NOT here — they keep the code. */
const TERMINAL_REFUSALS = new Set<string>(['promo_invalid', 'promo_already_redeemed', 'promo_wrong_account']);

interface PromoApplyResult {
  entitlement: {
    plan: 'essentials' | 'plus';
    plus: boolean;
    promoCode: string | null;
  };
}

/** Capture a promo code from the account-creation field. Newest wins. */
export function capturePromoCode(raw: string): void {
  const code = raw.trim();
  if (!code) return;
  try {
    localStorage.setItem(LS_PROMO, code);
  } catch {
    /* storage blocked — nothing to remember, nothing to redeem */
  }
}

/** The code this device is holding, or null. */
export function pendingPromoCode(): string | null {
  try {
    const code = localStorage.getItem(LS_PROMO);
    return code && code.trim() ? code.trim() : null;
  } catch {
    return null;
  }
}

/** Read the {error:{code}} out of an api() rejection — null when unparseable. */
function refusalCodeOf(e: unknown): string | null {
  const raw = e instanceof Error ? e.message : '';
  if (!raw.startsWith('{')) return null; // network failure, not an API answer
  try {
    const code = (JSON.parse(raw) as { error?: { code?: string } })?.error?.code;
    return typeof code === 'string' && code ? code : null;
  } catch {
    return null;
  }
}

/**
 * Redeem the stored code — call once this device holds a couple token.
 * Never throws, never error-toasts: true means the reward landed (the event
 * is already fired), false means nothing happened (no code, already applied,
 * refused, or the worker could not be reached — in which case the code is
 * kept for the next launch, and the refusal codes above are the only ones
 * that drop it).
 */
export async function applyPendingPromo(): Promise<boolean> {
  const code = pendingPromoCode();
  if (!code) return false;
  if (!getToken()) return false; // no couple token yet — wait for the session
  const coupleId = (() => {
    try {
      return localStorage.getItem('pear_couple_id') ?? '';
    } catch {
      return '';
    }
  })();
  const marker = JSON.stringify({ code, coupleId });
  try {
    const stored = localStorage.getItem(LS_PROMO_APPLIED);
    if (stored === marker) {
      // This couple already redeemed (or was refused for good on) this code —
      // a repeat tap must not re-POST. Drop the stale capture quietly.
      localStorage.removeItem(LS_PROMO);
      return false;
    }
  } catch {
    return false; // storage blocked — nothing to guard, nothing to redeem
  }
  try {
    const r = await api<PromoApplyResult>('/api/billing/promo', { body: { code } });
    localStorage.removeItem(LS_PROMO);
    localStorage.setItem(LS_PROMO_APPLIED, marker);
    window.dispatchEvent(new CustomEvent(PROMO_APPLIED_EVENT, { detail: { code } }));
    return true;
  } catch (e) {
    const refusal = refusalCodeOf(e);
    if (refusal && TERMINAL_REFUSALS.has(refusal)) {
      // Dead for this couple: forget it so it never fires again, and stay
      // silent — a bad code must not look like a broken sign-up.
      try {
        localStorage.removeItem(LS_PROMO);
        localStorage.setItem(LS_PROMO_APPLIED, marker);
      } catch {
        /* storage blocked — the POST is not retried this session anyway */
      }
      console.debug('[Pear] Promo not applied:', refusal);
      return false;
    }
    // Offline / cold worker: keep the code for the next launch. App.tsx's
    // one-attempt-per-launch ref stops it re-firing inside this session.
    console.debug('[Pear] Promo apply failed (kept for next launch):', e);
    return false;
  }
}